import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { Redirect, Switch } from 'react-router';
import { AnyAction } from 'redux';

import { SubscriptionType } from 'common/constants/subscription';
import { State } from 'common/interfaces/state';
import { ParametrizedAppRoute } from 'common/routing/parametrized-app-route';
import { SubscriptionParams } from 'routes/own-company-subscription';
import { NotFoundPage } from 'unit-error-pages/pages';
import { PeopleActions } from 'unit-people/actions/actions';
import { Company } from '../../units/account/interfaces/company';
import { OwnCompany } from '../../units/account/interfaces/own-company';
import { SubscriptionActions } from '../../units/subscription/actions/creators';
import { SpinnerPage } from '../spinner-page';


interface StateProps {
  isUserInfoLoaded: boolean;
  ownCompany: OwnCompany | null;
  companies: Company[];
  selectedSubscriptionType: SubscriptionType;
  selectedCompanyId: number;
  persistedSelectedCompanyId: number;
}

interface OwnProps {
  ownCompanyPath?: ParametrizedAppRoute<SubscriptionParams>;
  subscriptionType: SubscriptionType;
  companyId?: number;
}

interface DispatchProps {
  setSubscriptionAndCompany: (subscriptionType: SubscriptionType, companyId: number) => void;
  getCompanySubscriptionUsers: () => void;
  dropPeopleState: () => void;
}

interface Props extends OwnProps, StateProps, DispatchProps {
}

class CompanySubscriptionWrapComponent extends React.Component<Props> {
  public componentDidMount(): void {
    this.setSubscriptionAndCompanyIfNeeded();
    this.fetchCompanySubscriptionUsersIfNeeded(null);
  }

  public componentDidUpdate(prevProps: Props): void {
    this.setSubscriptionAndCompanyIfNeeded();
    this.fetchCompanySubscriptionUsersIfNeeded(prevProps);
  }

  public render(): JSX.Element {
    const { companies, companyId, ownCompany, isUserInfoLoaded } = this.props;
    const { selectedCompanyId, subscriptionType, children, ownCompanyPath } = this.props;
    const ownCompanyId = ownCompany && ownCompany.id;
    const hasAccess = (selectedCompanyId || !companyId)
      && Object.values(SubscriptionType).includes(subscriptionType);

    if (ownCompanyPath && companyId && companyId === ownCompanyId) {
      const redirectUrl = ownCompanyPath.url({ type: subscriptionType });
      return <Redirect to={redirectUrl} />;
    }

    if (!companies || !isUserInfoLoaded) {
      return <SpinnerPage />;
    }

    if (!hasAccess) {
      return <NotFoundPage />;
    }

    return (
      <Switch>
        {children}
      </Switch>
    );
  }

  public componentWillUnmount(): void {
    this.props.dropPeopleState();
  }

  setSubscriptionAndCompanyIfNeeded(): void {
    const {
      companyId,
      subscriptionType,
      selectedCompanyId,
      persistedSelectedCompanyId,
      selectedSubscriptionType,
      ownCompany,
    } = this.props;
    const ownCompanyId = ownCompany && ownCompany.id;
    const currentCompanyId = companyId || persistedSelectedCompanyId || ownCompanyId;

    if (
      currentCompanyId !== selectedCompanyId && currentCompanyId
      || subscriptionType !== selectedSubscriptionType
    ) {
      this.setSubscriptionAndCompany();
    }
  }

  fetchCompanySubscriptionUsersIfNeeded(prevProps: Props): void {
    const { selectedCompanyId, selectedSubscriptionType } = this.props;
    if (selectedCompanyId && selectedSubscriptionType
      && (
        !prevProps
        || selectedCompanyId !== prevProps.selectedCompanyId
        || selectedSubscriptionType !== prevProps.selectedSubscriptionType
      )
    ) {
      this.props.getCompanySubscriptionUsers();
    }
  }

  private setSubscriptionAndCompany(): void {
    const { subscriptionType, setSubscriptionAndCompany } = this.props;
    const companyId = this.getSelectedCompanyId();

    if (companyId) {
      setSubscriptionAndCompany(subscriptionType, companyId);
    }
  }

  private getSelectedCompanyId(): number {
    const { ownCompany, companies, companyId, persistedSelectedCompanyId, subscriptionType } = this.props;

    const selectedId = companyId || persistedSelectedCompanyId;
    const selectedCompany = companies.find(x => x.id === selectedId && subscriptionType in x.subscriptions);

    return selectedCompany
      ? selectedCompany.id
      : ownCompany?.id || companies[0]?.id;
  }
}

function mapStateToProps(state: State): StateProps {
  return {
    isUserInfoLoaded: !!state.account.id,
    ownCompany: state.account.ownedCompany,
    companies: state.account.companies || [],
    selectedCompanyId: state.account.selectedCompany?.id,
    persistedSelectedCompanyId: state.persistedStorage.selectedCompanyId,
    selectedSubscriptionType: state.account.selectedSubscriptionType,
  };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>): DispatchProps {
  return {
    setSubscriptionAndCompany: (subscriptionType, companyId) =>
      dispatch(SubscriptionActions.setSubscriptionTypeAndCompanyId(subscriptionType, companyId)),
    getCompanySubscriptionUsers: () => dispatch(PeopleActions.getCompanyPeople()),
    dropPeopleState: () => dispatch(PeopleActions.dropPeopleState()),
  };
}


const connector = connect(mapStateToProps, mapDispatchToProps);
export const CompanySubscriptionWrap = connector(CompanySubscriptionWrapComponent);
